import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { IObservationFull, IRequisition } from '@project/view-models';
import { RequisitionDetailsManagerService } from '../../services/requisition-details-manager.service';
import { MeasurementPreview } from '../../../../project/modules/session-details-shared/session-details-shared-patient-observations/session-details-shared-patient-observations.component';
import { AttachmentType, PlansService } from '../../../../project/services/plans.service';
import { NotificationsService } from '@lib/notifications';
import { TranslateService } from '@project/translate';
import { filter, map, take } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

interface UploadedFile {
  file: File;
  name: string;
  size: number;
  progress: number;
}

@Component({
  selector: 'app-session-details-patient-upload-files',
  templateUrl: './session-details-patient-document-upload-files.component.html',
  styleUrls: ['./session-details-patient-document-upload-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionDetailsPatientDocumentUploadFilesComponent implements OnInit {
  public observations$: Observable<IObservationFull[]> = this.requisitionDetailsManagerService.observations$;
  public readonly requisition$: Observable<IRequisition> = this.requisitionDetailsManagerService.requisition$;
  public readonly measurement$: Observable<MeasurementPreview> = this.requisitionDetailsManagerService.measurments$;
  public isLoading = false;
  private requisitionId: string;

  public uploadedFiles: UploadedFile[] = [];

  constructor(
    private requisitionDetailsManagerService: RequisitionDetailsManagerService,
    private cdr: ChangeDetectorRef,
    private PlansService: PlansService,
    private route: ActivatedRoute,
    private notificationsService: NotificationsService,
  ) {}

  ngOnInit(): void {
    this.initRequisitionId();
  }

  private initRequisitionId(): void {
    this.requisition$.pipe(take(1)).subscribe((requisition) => {
      if (requisition?.id) {
        this.requisitionId = requisition.id;
      } else {
        this.route.parent?.paramMap
          .pipe(
            map((p) => p.get('logId')),
            filter((id): id is string => !!id),
            take(1),
          )
          .subscribe((id) => (this.requisitionId = id));
      }
    });
  }

  onSelectFile(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '*/*';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file && this.whitelistFile(file)) {
        const uploaded: UploadedFile = {
          file,
          name: file.name,
          size: Math.round(file.size / 1024),
          progress: 100,
        };
        this.uploadedFiles.push(uploaded);
        this.cdr.markForCheck();
      }
    };
    input.click();
  }

  private whitelistFile(file: File): boolean {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      this.notificationsService.error({
        message: TranslateService.localize('errors.upload.files'),
      });
      return false;
    }
    return true;
  }

  onUpload(): void {
    if (!this.requisitionId) {
      this.notificationsService.error({
        message: TranslateService.localize('errors.unexpected-error'),
      });
      return;
    }

    this.isLoading = true;
    let completed = 0;
    const total = this.uploadedFiles.length;
    let hasErrors = false;

    this.uploadedFiles.forEach((file) => {
      this.PlansService.uploadFile(this.requisitionId, AttachmentType.MealPlan, file.file).subscribe({
        next: () => {
          file.progress = 100;
          completed++;
          this.checkIfAllUploadsFinished(completed, total, hasErrors);
        },
        error: () => {
          file.progress = 0;
          hasErrors = true;
          completed++;
          this.notificationsService.error({
            message: TranslateService.localize('upload.error').replace('{{file}}', file.name),
          });
          this.checkIfAllUploadsFinished(completed, total, hasErrors);
        },
      });
    });
  }

  private checkIfAllUploadsFinished(completed: number, total: number, hasErrors: boolean): void {
    if (completed >= total) {
      this.uploadedFiles = [];
      this.isLoading = false;
      this.cdr.markForCheck();

      this.notificationsService.success({
        message: TranslateService.localize('upload.success'),
      });
    }
  }

  onCancel(): void {
    this.uploadedFiles = [];
  }

  onDeleteFile(file: UploadedFile): void {
    this.uploadedFiles = this.uploadedFiles.filter((f) => f !== file);
  }
}
